/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import useArticleSection from "@/components/business/FillInTableContainer/hooks/useArticleSection";
import { ArticleSection } from "@/types/articleEntryData.types";
import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { IS_NOT_EDITABLE_SHELL_KEYS } from "@FillInTableContainer/components/FillInTable/constants/disabledShellBySection.constants";
import TableCreatorListBox from "@FillInTableContainer/shared/components/TableCreatorListBox/TableCreatorListBox";
import { AUTO_COMPANY_SHELL } from "@FillInTableContainer/shared/constants/autoCompleteListShell";
import { MULTI_SELECT_RIGHTS_SCOPE_SHELL_KEYS } from "@FillInTableContainer/shared/constants/multiSelectShell.constants";
import { ENABLE_GROUP_TABLE_KEY_LIST } from "@FillInTableContainer/shared/constants/tableCreatorGroupTable.constants";
import { useTableCreatorFocusStatus } from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorPositionInfo } from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useHandleTextFieldInputEvent } from "@FillInTableContainer/shared/hooks/useHandleTextFieldInputEvent";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import CopyButton from "./components/CopyButton/CopyButton";
import DeleteButton from "./components/DeleteButton/DeleteButton";
import ValidationMessage from "./components/ValidationMessage/ValidationMessage";
import * as S from "./TableCreatorTextFieldInput.style";

import type { EntryDataValueAndOptions } from "@/types/clinicalTrials.types";
import type { GroupTableKeyType } from "@FillInTableContainer/shared/types/tableCreator.types";

interface Props {
  articleSectionKey?: ArticleSection;
  placeholder: string;
  shellValue: EntryDataValueAndOptions;
  shellKey: string;
  groupTableKey: GroupTableKeyType;
  groupTableIndex: number;
  groupShellKey: string | null;
  tableKey: string | null;
  tableIndex: number;
  groupShellIndex: number;
  subGroupShellKey: string | null;
  subGroupShellIndex: number | null;
  shellIndex: number;
  valueIndex: number;
  changeTextFieldMarginBottom: (margin: number) => void;
  isMultipleLastValue: boolean;
  isMultipleValue?: boolean;
  openValidationInfo: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  closeValidationInfo: () => void;
}

// NOTE: Dropdown으로만 입력이 가능하도록 설정된 쉘 키 리스트
const DISABLE_TEXT_INSERT_SHELL_KEYS = [
  "Rights Scope",
  "Benefiting Axis",
  "Contributing Axis",
  "Partner Axis",
];

const TABLE_SHAPE_GROUP_TABLE_KEYS = ["Rights Scope Table"];
const DISABLE_DRAG_SELECT_SHELL_KEYS = [
  "Axis Direction",
  "Benefiting Axis",
  "Contributing Axis",
  "Rights Scope",
];

const TableCreatorTextFieldInput = forwardRef(
  (
    {
      articleSectionKey,
      placeholder,
      shellValue,
      shellKey,
      groupTableKey,
      groupTableIndex,
      groupShellKey,
      tableKey,
      tableIndex,
      groupShellIndex,
      subGroupShellKey,
      subGroupShellIndex,
      shellIndex,
      valueIndex,
      changeTextFieldMarginBottom,
      isMultipleLastValue,
      isMultipleValue,
      openValidationInfo,
    }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { info } = useTableCreatorPositionInfo();
    const { focusStatus } = useTableCreatorFocusStatus();
    const positionInfo = {
      groupTableKey: groupTableKey as GroupTableKeyType,
      groupTableIndex,
      tableKey,
      tableIndex,
      groupShellKey,
      groupShellIndex,
      subGroupShellKey,
      subGroupShellIndex,
      shellKey,
      shellIndex,
      valueIndex,
    };

    const isFocusedShell =
      info.tableIndex === tableIndex &&
      info.groupShellKey === groupShellKey &&
      info.groupShellIndex === groupShellIndex &&
      info.shellKey === shellKey &&
      info.shellIndex === shellIndex &&
      info.valueIndex === valueIndex;

    const {
      textFieldTextareaRef,
      targetListIndex,
      selectedValue,
      validationMessage,
      valueInfo,
      aiList,
      dictionaryList,
      dataSetList,
      isDictionaryList,
      isFocusInputField,
      isDataSetList,
      copyStatus,
      copyCurrentTextValue,
      checkIndexOfValueInDefaultAiList,
      changeSelectedValue,
      handleEnterValueKeyboard,
      handleChangedEnterValue,
      handleFocusTextField,
      handleFocusOutTextField,
      handleBlurTextField,
      deleteCurrentTextField,
      handleChangeDataSetValue,
      changeMultiSelectValues,
      getRawMultiSelectValues,
    } = useHandleTextFieldInputEvent(
      positionInfo,
      shellValue,
      articleSectionKey,
    );

    const isMultiSelect = MULTI_SELECT_RIGHTS_SCOPE_SHELL_KEYS.includes(
      shellKey as never,
    );

    const isDisableTextInsert =
      DISABLE_TEXT_INSERT_SHELL_KEYS.includes(shellKey);

    // Rights Scope Table 내의 쉘은 부모 overflow 클리핑을 피하기 위해 fixed 포지셔닝 사용
    const isFixed = groupTableKey === "Rights Scope Table";

    const draftArticleId = getPressIdFromURL();
    const articleSectionByClassification = useArticleSection();
    const articleSection = articleSectionKey || articleSectionByClassification;

    const [isTextDragging, setIsTextDragging] = useState(false);
    const cursorPositionRef = useRef<number | null>(null);
    const wasFocusedRef = useRef(false);

    // fixed 드롭다운 위치 계산용 ref & 상태
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const [dropdownPos, setDropdownPos] = useState({
      top: 0,
      left: 0,
      width: 0,
    });

    // 페인트 전에 위치를 계산해서 top:0,left:0 플래시 방지
    useLayoutEffect(() => {
      if (!isFixed || !isFocusInputField) return;

      const el = inputWrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 1,
        left: rect.left - 1,
        width: rect.width + 2,
      });
    }, [isFixed, isFocusInputField]);

    // 값이 변경될 때마다 (멀티셀렉 추가/제거 등) 드롭다운 위치 재계산
    useLayoutEffect(() => {
      if (!isFixed || !isFocusInputField) return;

      const el = inputWrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 1,
        left: rect.left - 1,
        width: rect.width + 2,
      });
    }, [isFixed, isFocusInputField, shellValue.value]);

    // 스크롤/리사이즈 시 위치 갱신
    useEffect(() => {
      if (!isFixed || !isFocusInputField) return;

      const updatePos = () => {
        const el = inputWrapperRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + 1,
          left: rect.left - 1,
          width: rect.width + 2,
        });
      };

      window.addEventListener("scroll", updatePos, true);
      window.addEventListener("resize", updatePos);
      return () => {
        window.removeEventListener("scroll", updatePos, true);
        window.removeEventListener("resize", updatePos);
      };
    }, [isFixed, isFocusInputField]);

    const { data: articleInfo } = useArticleInfoQuery(Number(draftArticleId));

    const enableGroupTableKeyList = ENABLE_GROUP_TABLE_KEY_LIST[articleSection];

    const isNotEditable =
      IS_NOT_EDITABLE_SHELL_KEYS[articleSection].includes(shellKey);

    const isEnableEdit = enableGroupTableKeyList[
      groupTableKey as keyof typeof enableGroupTableKeyList
    ].includes(articleInfo?.draftArticleStatus ?? "");

    useEffect(() => {
      const handleCheckClickOutside = (e: MouseEvent) => {
        // Multi-select 모드에서 드롭다운 아이템 클릭 시 드롭다운을 닫지 않음
        if (
          isMultiSelect &&
          (e.target as HTMLElement).closest('[data-dropdown="list-box"]')
        ) {
          return;
        }
        if (isFocusInputField && !isTextDragging) {
          handleFocusOutTextField();
        }
      };
      window.addEventListener("click", handleCheckClickOutside);

      return () => window.removeEventListener("click", handleCheckClickOutside);
    }, [
      handleFocusOutTextField,
      isFocusInputField,
      isMultiSelect,
      isTextDragging,
    ]);

    const saveCursorPosition = () => {
      try {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          if (
            textFieldTextareaRef.current &&
            textFieldTextareaRef.current.contains(range.startContainer)
          ) {
            const textLength =
              textFieldTextareaRef.current.textContent?.length || 0;
            cursorPositionRef.current = Math.min(range.startOffset, textLength);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("커서 위치 저장 중 오류 발생:", error);
      }
    };

    const restoreCursorPosition = () => {
      try {
        if (
          textFieldTextareaRef.current &&
          cursorPositionRef.current !== null
        ) {
          const selection = window.getSelection();
          if (selection) {
            const currentPosition =
              selection.rangeCount > 0
                ? selection.getRangeAt(0).startOffset
                : 0;

            if (currentPosition === 0) {
              if (
                !textFieldTextareaRef.current.firstChild &&
                textFieldTextareaRef.current.textContent === ""
              ) {
                textFieldTextareaRef.current.textContent = "";
              }

              const textNode =
                textFieldTextareaRef.current.firstChild ||
                textFieldTextareaRef.current;

              const textLength = textNode.textContent?.length || 0;
              const position = Math.min(cursorPositionRef.current, textLength);

              const range = document.createRange();
              range.setStart(textNode, position);
              range.setEnd(textNode, position);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("커서 위치 복원 중 오류 발생:", error);
      }
    };

    if (isNotEditable) {
      return (
        <S.TextFieldInputWrapper className="modality-textfield-height">
          <S.TextareaWrapper className="modality-textfield-height">
            <S.TextFieldTextarea
              placeholder={placeholder}
              defaultValue={selectedValue}
              ref={textFieldTextareaRef}
              $isModified={valueInfo.is_modified ?? true}
              $isMultipleLastValue={isMultipleLastValue}
              $disabled
              $isNotEditable={isNotEditable}
              $isFocusedShell={false}
              $isDisableTextInsert={isDisableTextInsert}
              className="modality-textfield-height"
            />
          </S.TextareaWrapper>
        </S.TextFieldInputWrapper>
      );
    }

    return (
      <S.TextFieldInputWrapper
        ref={inputWrapperRef}
        className="modality-textfield-height deal-la-sc-rights-scope-text-field-input-wrapper"
      >
        <S.TextareaWrapper className="modality-textfield-height deal-la-sc-rights-scope-textarea-wrapper">
          <S.TextFieldTextarea
            onFocus={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleFocusTextField(e);
              setIsTextDragging(true);
              if (!TABLE_SHAPE_GROUP_TABLE_KEYS.includes(groupTableKey)) {
                setTimeout(restoreCursorPosition, 0);
              }
            }}
            onBlur={() => {
              saveCursorPosition();
              handleBlurTextField();
              setIsTextDragging(false);
            }}
            onBeforeInput={(e) => {
              if (isDisableTextInsert) e.preventDefault();
            }}
            onInput={(e) => {
              handleChangedEnterValue(e);
              setTimeout(saveCursorPosition, 0);
            }}
            onKeyDown={handleEnterValueKeyboard}
            onMouseUp={() => {
              saveCursorPosition();
            }}
            onMouseDown={(e) => {
              if (!TABLE_SHAPE_GROUP_TABLE_KEYS.includes(groupTableKey)) return;
              e.stopPropagation();
              wasFocusedRef.current =
                document.activeElement === e.currentTarget;
              if (DISABLE_DRAG_SELECT_SHELL_KEYS.includes(shellKey)) {
                e.preventDefault();
              }
              e.currentTarget.focus();
            }}
            onKeyUp={() => {
              saveCursorPosition();
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (!TABLE_SHAPE_GROUP_TABLE_KEYS.includes(groupTableKey)) return;
              if (wasFocusedRef.current) return;
              const sel = window.getSelection();
              if (sel && sel.toString().length > 0) return;
              if (!textFieldTextareaRef.current) return;
              const textNode =
                textFieldTextareaRef.current.firstChild ||
                textFieldTextareaRef.current;
              const textLength = textNode.textContent?.length || 0;
              const range = document.createRange();
              range.setStart(textNode, textLength);
              range.setEnd(textNode, textLength);
              sel?.removeAllRanges();
              sel?.addRange(range);
              cursorPositionRef.current = textLength;
            }}
            placeholder={placeholder}
            defaultValue={selectedValue}
            ref={textFieldTextareaRef}
            contentEditable={isEnableEdit}
            $isModified={valueInfo.is_modified ?? true}
            $isMultipleLastValue={isMultipleLastValue}
            $isFocusedShell={isFocusedShell}
            $disabled={!isEnableEdit}
            $isDisableTextInsert={isDisableTextInsert}
            $isNotEditable={isNotEditable}
            className="modality-textfield-height deal-la-sc-rights-scope-textarea"
          />
        </S.TextareaWrapper>
        {valueInfo.text && (
          <CopyButton
            copyStatus={copyStatus}
            copyCurrentTextValue={copyCurrentTextValue}
          />
        )}
        {valueInfo.text && (
          <DeleteButton deleteCurrentTextField={deleteCurrentTextField} />
        )}
        {isFocusInputField &&
          !AUTO_COMPANY_SHELL.includes(shellKey) &&
          focusStatus.container !== "noFocus" && (
            <TableCreatorListBox
              ref={ref}
              aiList={aiList}
              shellKey={shellKey}
              changeSelectedValue={changeSelectedValue}
              changeTextFieldMarginBottom={changeTextFieldMarginBottom}
              targetListIndex={targetListIndex}
              dictionaryList={dictionaryList}
              isDictionaryList={isDictionaryList}
              isDataSetList={isDataSetList}
              dataSetList={dataSetList}
              checkIndexOfValueInDefaultAiList={
                checkIndexOfValueInDefaultAiList
              }
              handleChangeDataSetValue={handleChangeDataSetValue}
              isMultiSelect={isMultiSelect}
              multiSelectCurrentValues={getRawMultiSelectValues()}
              changeMultiSelectValues={changeMultiSelectValues}
              isFixed={isFixed}
              fixedTop={dropdownPos.top}
              fixedLeft={dropdownPos.left}
              fixedWidth={dropdownPos.width}
            />
          )}
        {validationMessage !== null && !!selectedValue && (
          <ValidationMessage
            messageInfo={validationMessage}
            openValidationInfo={openValidationInfo}
            isMultipleValue={isMultipleValue}
          />
        )}
      </S.TextFieldInputWrapper>
    );
  },
);

export default TableCreatorTextFieldInput;
